/**
 * Pure parser for the Striim `MON;` (cluster-wide) Tungsten response.
 *
 * Kept dependency-free (no api/transport imports) so it can be unit-tested in isolation. The AI
 * sidebar's component_monitoring_tool runs `MON;` in the browser and runs this on the result,
 * returning only a compact metrics summary to the LLM instead of the full envelope.
 *
 * Wire shape of the `MON;` response (see Platform `CluiMonitorView` / `MonitorCommandsCommon`):
 *   [{
 *     command: "MON",
 *     executionStatus: "Success",
 *     responseCode: 200,
 *     output: {
 *       striimApplications: [{ entityType, fullName, statusChange, rate, sourceRate, cpuRate,
 *                              numServers, latestActivity }],
 *       striimClusterNodes:  [{ entityType, name, version, freeMemory, cpuRate, uptime }],
 *       elasticsearch:       { elasticsearchReceiveThroughput, ... }
 *     }
 *   }]
 *
 * This is the cluster-wide `MON;` form only. A single-component `MON <component>;` returns a
 * different (recursive tree) shape — use `buildMonComponentSummary` for that.
 */

export interface MonParseError {
    error: string;
    error_type: string;
}

export interface MonApplicationMetrics {
    name: string;
    status: string;
    rate: string;
    sourceRate: string;
    cpuRate: string;
    numServers: string;
    latestActivity: string;
}

export interface MonNodeMetrics {
    name: string;
    cpuRate: string;
    freeMemory: string;
    uptime: string;
    version: string;
}

export interface MonSummary {
    application_count: number;
    applications: MonApplicationMetrics[];
    nodes: MonNodeMetrics[];
    elasticsearch: Record<string, unknown>;
}

/**
 * Build a compact MON summary (per-application metrics, per-node metrics, elasticsearch throughput)
 * from the raw `MON;` response. Inputs carrying an `error` field (e.g. transport failures or auth
 * problems) are passed through unchanged so the agent can reason about them; any other malformed
 * input yields a generic parse error.
 */
export function buildMonSummary(data: unknown): MonSummary | MonParseError {
    if (data !== null && typeof data === "object" && !Array.isArray(data) && "error" in data) {
        return data as MonParseError;
    }

    // The Tungsten endpoint wraps results in an array of command envelopes; the MON metrics live
    // on the first envelope's `output`.
    const envelope = Array.isArray(data) ? data[0] : data;
    if (envelope === null || typeof envelope !== "object") {
        return { error: "Invalid MON API response structure", error_type: "APIError" };
    }

    const output = (envelope as Record<string, unknown>).output ?? envelope;
    if (output === null || typeof output !== "object") {
        return { error: "Invalid MON API response structure", error_type: "APIError" };
    }

    const out = output as Record<string, unknown>;
    const rawApps = Array.isArray(out.striimApplications) ? out.striimApplications : [];
    const rawNodes = Array.isArray(out.striimClusterNodes) ? out.striimClusterNodes : [];

    const applications = rawApps
        .map(entry => entry as Record<string, unknown>)
        .map(entry => ({
            name: String(entry?.fullName ?? ""),
            status: String(entry?.statusChange ?? "N/A"),
            rate: String(entry?.rate ?? "N/A"),
            sourceRate: String(entry?.sourceRate ?? "N/A"),
            cpuRate: String(entry?.cpuRate ?? ""),
            numServers: String(entry?.numServers ?? "0"),
            latestActivity: String(entry?.latestActivity ?? "")
        }))
        .filter(app => !app.name.includes("System$"))
        .sort((a, b) => a.name.localeCompare(b.name));

    const nodes = rawNodes
        .map(entry => entry as Record<string, unknown>)
        .map(entry => ({
            name: String(entry?.name ?? ""),
            cpuRate: String(entry?.cpuRate ?? ""),
            freeMemory: String(entry?.freeMemory ?? ""),
            uptime: String(entry?.uptime ?? ""),
            version: String(entry?.version ?? "")
        }));

    const elasticsearch =
        out.elasticsearch !== null && typeof out.elasticsearch === "object"
            ? (out.elasticsearch as Record<string, unknown>)
            : {};

    return { application_count: applications.length, applications, nodes, elasticsearch };
}

export interface MonComponentNode {
    name: string;
    entityType: string;
    status: string;
    rate: string;
    sourceRate: string;
    cpuRate: string;
    numServers: string;
    latestActivity: string;
    components?: MonComponentNode[];
}

/** Normalise a single `MON <component>;` node, recursing into its children. */
function mapMonNode(entry: Record<string, unknown>): MonComponentNode {
    // Container nodes carry children under `applicationComponents` (APPLICATION) or `flowComponents`
    // (FLOW); flatten both into a single uniform `components` list.
    const children = [
        ...(Array.isArray(entry.applicationComponents) ? entry.applicationComponents : []),
        ...(Array.isArray(entry.flowComponents) ? entry.flowComponents : [])
    ];

    const node: MonComponentNode = {
        name: String(entry?.fullName ?? ""),
        entityType: String(entry?.entityType ?? ""),
        status: String(entry?.statusChange ?? ""),
        rate: String(entry?.rate ?? "N/A"),
        sourceRate: String(entry?.sourceRate ?? "N/A"),
        cpuRate: String(entry?.cpuRate ?? ""),
        numServers: String(entry?.numServers ?? "0"),
        latestActivity: String(entry?.latestActivity ?? "")
    };

    if (children.length > 0) {
        node.components = children.map(child => mapMonNode(child as Record<string, unknown>));
    }
    return node;
}

/**
 * Build a compact metrics tree from a single-component `MON <component>;` response. The output is a
 * recursive node (APPLICATION → FLOW → SOURCE/STREAM/TARGET), each carrying the same metric fields,
 * with field names normalised to match {@link buildMonSummary} (`fullName`→`name`,
 * `statusChange`→`status`). Inputs carrying an `error` field are passed through unchanged.
 */
export function buildMonComponentSummary(data: unknown): MonComponentNode | MonParseError {
    if (data !== null && typeof data === "object" && !Array.isArray(data) && "error" in data) {
        return data as MonParseError;
    }

    const envelope = Array.isArray(data) ? data[0] : data;
    if (envelope === null || typeof envelope !== "object") {
        return { error: "Invalid MON API response structure", error_type: "APIError" };
    }

    const output = (envelope as Record<string, unknown>).output ?? envelope;
    if (output === null || typeof output !== "object") {
        return { error: "Invalid MON API response structure", error_type: "APIError" };
    }

    return mapMonNode(output as Record<string, unknown>);
}
